import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CalendarDays, Check, MapPin } from "lucide-react";
import { RETREAT } from "@/content";

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, new Date(target).getTime() - now);
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    minutes: Math.floor((diff / 60000) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

export default function Retreat({ onBook }) {
  const t = useCountdown(RETREAT.countdownTo);
  const units = [
    ["Days", t.days],
    ["Hours", t.hours],
    ["Min", t.minutes],
    ["Sec", t.seconds],
  ];

  return (
    <section id="retreat" data-testid="upcoming-retreat-section" className="relative overflow-hidden scroll-mt-20">
      <img
        src="/images/hero-bali.jpg"
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-25"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#12281D] via-[#0F2018]/90 to-[#12281D]" />
      <div className="relative mx-auto max-w-6xl px-5 py-20 lg:py-28">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-center"
        >
          <p className="overline-gold mb-4">Upcoming Retreat</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#FBF9F1]">
            {RETREAT.name} <span className="gold-text">— {RETREAT.place}</span>
          </h2>
          <p data-testid="retreat-mantra" className="mt-4 font-script text-3xl sm:text-4xl text-[#D4AF37]">
            {RETREAT.mantra}
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-[#D1DDD5]">
            <span data-testid="retreat-dates" className="inline-flex items-center gap-2">
              <CalendarDays size={15} className="text-[#D4AF37]" />
              {RETREAT.dates} · {RETREAT.length}
            </span>
            <span data-testid="retreat-location" className="inline-flex items-center gap-2">
              <MapPin size={15} className="text-[#D4AF37]" />
              {RETREAT.location}
            </span>
          </div>
        </motion.div>

        <motion.div
          data-testid="retreat-countdown"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="mt-10 flex justify-center gap-3 sm:gap-5"
        >
          {units.map(([label, val]) => (
            <div
              key={label}
              className="w-[72px] sm:w-24 rounded-2xl border border-[#D4AF37]/30 bg-[#1B3B2B]/70 backdrop-blur-sm py-4 text-center"
            >
              <p className="font-serif text-3xl sm:text-4xl text-[#EED88F]">
                {String(val).padStart(2, "0")}
              </p>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#D1DDD5]/70 mt-1">{label}</p>
            </div>
          ))}
        </motion.div>

        <div className="mt-12 grid lg:grid-cols-2 gap-6 items-stretch">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7 }}
            className="rounded-3xl border border-[#D4AF37]/25 bg-[#1B3B2B]/60 p-8 sm:p-10"
          >
            <p className="overline-gold mb-5">Retreat Highlights</p>
            <ul className="space-y-4">
              {RETREAT.highlights.map((h) => (
                <li key={h} className="flex items-start gap-3 text-[15px] text-[#D1DDD5]">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-[#D4AF37]/20 flex items-center justify-center shrink-0">
                    <Check size={12} className="text-[#D4AF37]" />
                  </span>
                  {h}
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: 0.12 }}
            className="rounded-3xl bg-[#F4EFE6] text-[#232D27] p-8 sm:p-10 flex flex-col justify-between"
          >
            <div>
              <p className="overline-gold mb-4">Investment</p>
              <p data-testid="retreat-price" className="font-serif text-4xl sm:text-5xl text-[#1B3B2B]">
                $2,600
              </p>
              <p className="text-sm text-[#3A4D41] mt-2">per person · all rooms · 8 nights / 9 days</p>
              <p className="mt-5 text-[15px] leading-relaxed text-[#3A4D41]">
                Begin with a relaxed, informational discovery call — we'll answer every question and
                make sure Bali is the right yes for you.
              </p>
            </div>
            <button
              data-testid="retreat-book-call-button"
              onClick={onBook}
              className="btn-gold mt-8 w-full sm:w-auto"
            >
              <CalendarDays size={16} />
              Book a Discovery Call
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
